import { Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Step from "@mui/material/Step";
import StepContent from "@mui/material/StepContent";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import * as React from "react";

import { trackEvent } from "utils/tracking";

const steps = [
  {
    label: "Complete Your Profile",
    description: `Fill in a few details to get personalized brand recommendations tailored to your content.`,
  },
  {
    label: "Connect Your Instagram",
    description:
      "Connect with Bento on Instagram to curate your brand pitch list with your favorite creators, and get pitching tips and support from our team anytime!",
  },
  {
    label: "Create a Pitch Template",
    description:
      "Upload your own template or use our tools to help you write one. This email will be sent to brands you're interested in working with.",
  },
  {
    label: "Send Your First Pitch",
    description: `Select a brand, make any edits to your email, and hit send—all through Bento.`,
  },
];

const getStepName = (index: number) => {
  if (index === 0) {
    return "Profile";
  } else if (index === 1) {
    return "Connect Your Instagram";
  } else if (index === 2) {
    return "Create Template";
  } else if (index === 3) {
    return "Email Brand";
  }
};

export default function OnboardingStepper() {
  const [activeStep, setActiveStep] = React.useState(0);
  const [expandedSteps, setExpandedSteps] = React.useState<number[]>([0]);
  const { currentOrg, profile } = React.useContext(OrganizationUserContext);
  const {
    setProfileDialogOpen,
    setCreateTemplateOnboardingDialogOpen,
    setEmailBrandDialogOpen,
    setInstagramOnboardingDialogOpen,
    incompleteProfile,
    noTemplates,
  } = React.useContext(OnboardingDialogsContext);

  const handleStart = (index: number) => {
    trackEvent("Home Page Onboarding Step Get Started Clicked", {
      step: getStepName(index),
    });
    if (index === 0) {
      setProfileDialogOpen(true);
    } else if (index === 1) {
      setInstagramOnboardingDialogOpen(true);
    } else if (index === 2) {
      setCreateTemplateOnboardingDialogOpen(true);
    } else if (index === 3) {
      setEmailBrandDialogOpen(true);
    }
  };

  React.useEffect(() => {
    if (incompleteProfile) {
      setActiveStep(0);
      setExpandedSteps([0]);
    } else if (!profile?.instagramUrl) {
      setActiveStep(1);
      setExpandedSteps([1]);
    } else if (noTemplates) {
      setActiveStep(2);
      setExpandedSteps([2]);
    } else {
      setActiveStep(3);
      setExpandedSteps([3]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, noTemplates, incompleteProfile, profile?.instagramUrl]);

  return (
    <Box>
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step expanded={expandedSteps.includes(index)} key={step.label}>
            <StepLabel>
              <Typography
                variant="h6"
                sx={{
                  fontSize: 15,
                  cursor: index === activeStep ? "inherit" : "pointer",
                }}
                onClick={() => {
                  trackEvent("Home Page Onboarding Step Clicked", {
                    step: getStepName(index),
                  });
                  setExpandedSteps((prev) => {
                    const copy = [...prev];
                    const idx = copy.indexOf(index);
                    if (idx !== -1) {
                      copy.splice(idx, 1);
                    } else {
                      copy.push(index);
                    }
                    return copy;
                  });
                }}
              >
                {step.label}
              </Typography>
            </StepLabel>
            <StepContent>
              <Typography>{step.description}</Typography>
              <Box sx={{ mb: 2 }}>
                <Tooltip
                  title={
                    noTemplates && index === 2
                      ? "Please create a template first before sending you first email"
                      : ""
                  }
                >
                  <Box component="span">
                    <Button
                      variant="contained"
                      onClick={() => {
                        handleStart(index);
                      }}
                      sx={{ mt: 1, mr: 1 }}
                      disabled={noTemplates && index === 3}
                    >
                      Start
                    </Button>
                  </Box>
                </Tooltip>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}
